// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Resource Offers
// Two views: own org's offers + all available resources from others
// Click into any offer to see full details + org contact info
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, Modal, Tabs, Stack, Divider, SimpleGrid,
} from '../../../components/gab/GabComponents';
import { useDisclosure } from '../../../components/gab/GabHooks';
import { notifications } from '../../../lib/notifications';
import {
  IconPlus, IconRefresh, IconPackage, IconList, IconArrowLeft,
  IconUser, IconPhone, IconMail,
} from '@tabler/icons-react';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMyResourceOffers } from '../../../hooks/useMyOrgData';
import { useMainStreetStore } from '../../../store/mainstreetStore';
import { useMainStreetOpsStore, type MSResourceOffer } from '../../../store/mainstreetOpsStore';
import { useAuthStore } from '../../../store/authStore';
import { OFFER_TYPES } from '../constants';
import { fmtDate } from '../../../lib/dateFormat';

const OFFER_SELECT = OFFER_TYPES.map(t => ({ value: t.id, label: t.label }));

const STATUS_COLORS: Record<string, string> = {
  offered: 'blue', accepted: 'green', fulfilled: 'teal', declined: 'red',
};

function offerLabel(id: string): string {
  return OFFER_TYPES.find(t => t.id === id)?.label ?? id;
}

export default function PortalResourceOffers() {
  const myOrg = useMyOrg();
  const myOffers = useMyResourceOffers();
  const organizations = useMainStreetStore(s => s.organizations);
  const contacts = useMainStreetStore(s => s.contacts);
  const { resourceOffers, loading, loadResourceOffers, createResourceOffer } = useMainStreetOpsStore();
  const { activeIncident, user } = useAuthStore();

  const PAGE_SIZE = 25;
  const [allPage, setAllPage] = useState(1);
  const [minePage, setMinePage] = useState(1);

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [offerType, setOfferType] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [detailOffer, setDetailOffer] = useState<MSResourceOffer | null>(null);

  // My org's offers
  const myOffersSorted = useMemo(() =>
    [...myOffers].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? '')),
    [myOffers],
  );

  // All available offers (only "offered" status — not yet accepted/fulfilled/declined)
  const allOffers = useMemo(() => {
    let list = resourceOffers.filter(r => r.status === 'offered');
    if (activeIncident) {
      list = list.filter(r => String(r.incidentId) === String(activeIncident.id));
    }
    if (filterType !== 'all') list = list.filter(r => r.offerType === filterType);
    return [...list].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
  }, [resourceOffers, activeIncident, filterType]);

  useEffect(() => { setAllPage(1); }, [allOffers.length]);
  useEffect(() => { setMinePage(1); }, [myOffersSorted.length]);
  const allTotalPages = Math.max(1, Math.ceil(allOffers.length / PAGE_SIZE));
  const allPageRows = allOffers.slice((allPage - 1) * PAGE_SIZE, allPage * PAGE_SIZE);
  const mineTotalPages = Math.max(1, Math.ceil(myOffersSorted.length / PAGE_SIZE));
  const minePageRows = myOffersSorted.slice((minePage - 1) * PAGE_SIZE, minePage * PAGE_SIZE);

  const typeFilterOptions = [
    { value: 'all', label: 'All Types' },
    ...OFFER_SELECT,
  ];

  // Get org info and contacts for detail view
  function getOrgInfo(orgId: string) {
    const org = organizations.find(o => String(o.id) === orgId);
    const orgContacts = contacts.filter(c => c.orgId === orgId);
    return { org, contacts: orgContacts };
  }

  async function handleCreate() {
    if (!offerType) {
      notifications.show({ color: 'red', title: 'Required', message: 'Offer type is required.' });
      return;
    }
    if (!myOrg) return;
    setSaving(true);
    await createResourceOffer({
      orgId: String(myOrg.id),
      orgName: myOrg.name,
      incidentId: activeIncident ? String(activeIncident.id) : '',
      offerType,
      description: description.trim(),
      quantity: quantity.trim(),
      status: 'offered',
      offeredBy: user?.fullName ?? user?.email ?? 'Unknown',
      createdAt: new Date().toISOString(),
      notes: notes.trim(),
    });
    setSaving(false);
    notifications.show({ color: 'green', title: 'Created', message: 'Resource offer submitted.' });
    setOfferType(''); setDescription(''); setQuantity(''); setNotes('');
    closeCreate();
  }

  // ── Detail view ────────────────────────────────────────────────
  if (detailOffer) {
    const { org, contacts: orgContacts } = getOrgInfo(detailOffer.orgId);
    return (
      <div>
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => setDetailOffer(null)} mb="md">
          Back to Resources
        </Button>

        <Paper withBorder p="lg" mb="md">
          <Group justify="space-between" align="flex-start" wrap="wrap" mb="md">
            <div>
              <Badge size="sm" variant="light" mb={4}>{offerLabel(detailOffer.offerType)}</Badge>
              <Title order={4}>{detailOffer.orgName || 'Unknown Organization'}</Title>
            </div>
            <Badge size="md" color={STATUS_COLORS[detailOffer.status] ?? 'gray'}>
              {detailOffer.status}
            </Badge>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Description</Text>
              <Text size="sm">{detailOffer.description || 'No description provided'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Quantity</Text>
              <Text size="sm">{detailOffer.quantity || '-'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Offered By</Text>
              <Text size="sm">{detailOffer.offeredBy || '-'}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Date</Text>
              <Text size="sm">{fmtDate(detailOffer.createdAt)}</Text>
            </div>
          </SimpleGrid>

          {detailOffer.notes && (
            <>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Notes</Text>
              <Text size="sm" mb="md">{detailOffer.notes}</Text>
            </>
          )}
        </Paper>

        {/* Organization Contact Info */}
        <Paper withBorder p="lg">
          <Title order={5} mb="sm">Contact Information</Title>
          {org ? (
            <Stack gap="sm">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <div>
                  <Text size="xs" c="dimmed">Organization</Text>
                  <Text size="sm" fw={500}>{org.name}</Text>
                </div>
                {org.primaryContact && (
                  <div>
                    <Text size="xs" c="dimmed">Primary Contact</Text>
                    <Group gap={4}>
                      <IconUser size={14} color="gray" />
                      <Text size="sm">{org.primaryContact}</Text>
                    </Group>
                  </div>
                )}
                {org.phone && (
                  <div>
                    <Text size="xs" c="dimmed">Phone</Text>
                    <Group gap={4}>
                      <IconPhone size={14} color="gray" />
                      <Text size="sm">{org.phone}</Text>
                    </Group>
                  </div>
                )}
                {org.email && (
                  <div>
                    <Text size="xs" c="dimmed">Email</Text>
                    <Group gap={4}>
                      <IconMail size={14} color="gray" />
                      <Text size="sm">{org.email}</Text>
                    </Group>
                  </div>
                )}
                {org.address && (
                  <div>
                    <Text size="xs" c="dimmed">Address</Text>
                    <Text size="sm">
                      {org.address}{org.city ? `, ${org.city}` : ''}{org.state ? ` ${org.state}` : ''}{org.zip ? ` ${org.zip}` : ''}
                    </Text>
                  </div>
                )}
              </SimpleGrid>

              {orgContacts.length > 0 && (
                <>
                  <Divider my="xs" />
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">Organization Contacts</Text>
                  {orgContacts.map(c => (
                    <Paper key={c.id} withBorder p="sm" radius="sm">
                      <Group gap="lg" wrap="wrap">
                        <div>
                          <Text size="sm" fw={500}>{c.name || 'Unnamed'}</Text>
                          {c.title && <Text size="xs" c="dimmed">{c.title}</Text>}
                        </div>
                        {c.phone && (
                          <Group gap={4}>
                            <IconPhone size={12} color="gray" />
                            <Text size="xs">{c.phone}</Text>
                          </Group>
                        )}
                        {c.email && (
                          <Group gap={4}>
                            <IconMail size={12} color="gray" />
                            <Text size="xs">{c.email}</Text>
                          </Group>
                        )}
                      </Group>
                    </Paper>
                  ))}
                </>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">Contact information not available</Text>
          )}
        </Paper>
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────

  function renderTable(offers: typeof allOffers, showOrg: boolean) {
    return (
      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                {showOrg && <th style={{ padding: '8px 12px', textAlign: 'left' }}>Organization</th>}
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Qty</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Offered By</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {offers.length === 0 && (
                <tr>
                  <td colSpan={showOrg ? 7 : 6} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loading ? 'Loading...' : 'No resource offers'}
                  </td>
                </tr>
              )}
              {offers.map(offer => (
                <tr key={offer.id}
                  style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                  onClick={() => setDetailOffer(offer)}>
                  {showOrg && <td style={{ padding: '8px 12px', fontWeight: 500 }}>{offer.orgName || '-'}</td>}
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="xs" variant="light">{offerLabel(offer.offerType)}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', maxWidth: 250 }}>
                    <Text size="xs" lineClamp={2}>{offer.description || '-'}</Text>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{offer.quantity || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <Badge size="xs" color={STATUS_COLORS[offer.status] ?? 'gray'}>
                      {offer.status || '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px' }}>{offer.offeredBy || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fmtDate(offer.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>
    );
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Resources</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => loadResourceOffers()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Submit Offer
          </Button>
        </Group>
      </Group>

      <Tabs defaultValue="all">
        <Tabs.List mb="md">
          <Tabs.Tab value="all" leftSection={<IconList size={14} />}>
            Available Resources ({allOffers.length})
          </Tabs.Tab>
          <Tabs.Tab value="mine" leftSection={<IconPackage size={14} />}>
            My Offers ({myOffersSorted.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="all">
          <Group mb="md" gap="sm">
            <Select data={typeFilterOptions} value={filterType}
              onChange={v => setFilterType(v ?? 'all')} size="xs" w={220} />
          </Group>
          <Text size="xs" c="dimmed" mb="xs">
            {allOffers.length} offer{allOffers.length !== 1 ? 's' : ''} from all organizations — click a row for details
          </Text>
          {renderTable(allPageRows, true)}
          {allTotalPages > 1 && (
            <Group justify="center" mt="sm" gap="xs">
              <Button variant="subtle" size="xs" disabled={allPage === 1} onClick={() => setAllPage(1)}>««</Button>
              <Button variant="subtle" size="xs" disabled={allPage === 1} onClick={() => setAllPage(p => p - 1)}>‹ Prev</Button>
              {Array.from({ length: Math.min(allTotalPages, 7) }, (_, i) => {
                const p = allTotalPages <= 7 ? i + 1 : allPage <= 4 ? i + 1 : allPage >= allTotalPages - 3 ? allTotalPages - 6 + i : allPage - 3 + i;
                return <Button key={p} variant={p === allPage ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setAllPage(p)}>{p}</Button>;
              })}
              <Button variant="subtle" size="xs" disabled={allPage === allTotalPages} onClick={() => setAllPage(p => p + 1)}>Next ›</Button>
              <Button variant="subtle" size="xs" disabled={allPage === allTotalPages} onClick={() => setAllPage(allTotalPages)}>»»</Button>
            </Group>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="mine">
          <Text size="xs" c="dimmed" mb="xs">{myOffersSorted.length} offer{myOffersSorted.length !== 1 ? 's' : ''} from {myOrg?.name}</Text>
          {renderTable(minePageRows, false)}
          {mineTotalPages > 1 && (
            <Group justify="center" mt="sm" gap="xs">
              <Button variant="subtle" size="xs" disabled={minePage === 1} onClick={() => setMinePage(1)}>««</Button>
              <Button variant="subtle" size="xs" disabled={minePage === 1} onClick={() => setMinePage(p => p - 1)}>‹ Prev</Button>
              {Array.from({ length: Math.min(mineTotalPages, 7) }, (_, i) => {
                const p = mineTotalPages <= 7 ? i + 1 : minePage <= 4 ? i + 1 : minePage >= mineTotalPages - 3 ? mineTotalPages - 6 + i : minePage - 3 + i;
                return <Button key={p} variant={p === minePage ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setMinePage(p)}>{p}</Button>;
              })}
              <Button variant="subtle" size="xs" disabled={minePage === mineTotalPages} onClick={() => setMinePage(p => p + 1)}>Next ›</Button>
              <Button variant="subtle" size="xs" disabled={minePage === mineTotalPages} onClick={() => setMinePage(mineTotalPages)}>»»</Button>
            </Group>
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Create Modal — org auto-filled */}
      <Modal opened={createOpened} onClose={closeCreate} title="Submit Resource Offer" size="lg">
        <Text size="sm" fw={500} mb="sm" c="blue">
          Offering from: {myOrg?.name}
        </Text>

        <Select label="Offer Type" data={OFFER_SELECT} value={offerType}
          onChange={v => setOfferType(v ?? '')} placeholder="Select type"
          size="sm" mb="sm" required />

        <Textarea label="Description" value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Describe the resource being offered..."
          size="sm" mb="sm" autosize minRows={3} />

        <TextInput label="Quantity" value={quantity}
          onChange={e => setQuantity(e.target.value)}
          placeholder="e.g. 3 generators, 50 meals/day" size="sm" mb="sm" />

        <Textarea label="Notes" value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Additional notes..." size="sm" mb="md" autosize minRows={2} />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={closeCreate}>Cancel</Button>
          <Button loading={saving} onClick={handleCreate}
            leftSection={<IconPackage size={16} />}>Submit Offer</Button>
        </Group>
      </Modal>
    </div>
  );
}
